import { tableOperations } from "./agent-tables";
import { z } from "zod";

export const canvasCoordinate = z.number().finite().min(-100000).max(100000);
export const canvasLength = z.number().finite().positive().max(10000);
export const canvasShapeId = z.string().regex(/^shape:[a-zA-Z0-9_-]{1,100}$/);
const point = z.object({ x: canvasCoordinate, y: canvasCoordinate }).strict();
const points = z.array(point).min(2).max(1000);
const parent = {
  parentId: z.string().regex(/^(shape|page):[a-zA-Z0-9_-]{1,100}$/),
};
const placed = {
  id: canvasShapeId,
  ...parent,
  x: canvasCoordinate,
  y: canvasCoordinate,
};
const hex = z.string().regex(/^#[a-fA-F0-9]{6}$/);
export const canvasColors = z.enum([
  "black",
  "grey",
  "light-violet",
  "violet",
  "blue",
  "light-blue",
  "yellow",
  "orange",
  "green",
  "light-green",
  "light-red",
  "red",
  "white",
]);
const style = {
  color: hex.default("#25343b"),
  weight: z.number().min(0.25).max(24).default(1.5),
};
const size = z.enum(["s", "m", "l", "xl"]);
const ids = z.array(canvasShapeId).min(1).max(100);
const text = z.string().max(12000);
const rows = z
  .array(z.array(z.string().max(4000)).min(1).max(20))
  .min(1)
  .max(200);
const range = z
  .object({
    startRow: z.number().int().nonnegative(),
    startColumn: z.number().int().nonnegative(),
    endRow: z.number().int().nonnegative(),
    endColumn: z.number().int().nonnegative(),
  })
  .strict();
const cellStyle = z
  .object({
    fontFamily: z.enum(["Arial", "Georgia", "monospace"]).optional(),
    fontSize: z.number().min(10).max(32).optional(),
    textColor: hex.optional(),
    fill: hex.optional(),
    border: hex.optional(),
    align: z.enum(["left", "center", "right"]).optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
  })
  .strict();
const paragraph = z
  .object({
    text,
    kind: z
      .enum(["paragraph", "heading", "bullet", "numbered", "quote"])
      .default("paragraph"),
    level: z.number().int().min(1).max(3).default(1),
    bold: z.boolean().default(false),
    italic: z.boolean().default(false),
    align: z.enum(["left", "center", "right"]).default("left"),
  })
  .strict();

/** Product actions, independent of the model and host. Every field reaches the same board validator. */
export const workspaceOperationSchemas = [
  z
    .object({
      type: z.literal("create_sheet"),
      id: canvasShapeId,
      pageId: z.string().regex(/^page:[a-zA-Z0-9_-]{1,100}$/),
      name: z.string().min(1).max(120),
      preset: z.enum(["A4", "A3", "Letter", "16:9", "4:3", "1:1", "Custom"]),
      landscape: z.boolean().default(true),
      widthMm: z.number().min(50).max(2000).optional(),
      heightMm: z.number().min(50).max(2000).optional(),
      scale: z.number().min(0).max(100000).default(0),
      x: canvasCoordinate,
      y: canvasCoordinate,
    })
    .strict(),
  z
    .object({
      type: z.literal("resize_sheet"),
      id: canvasShapeId,
      preset: z.enum(["A4", "A3", "Letter", "16:9", "4:3", "1:1", "Custom"]),
      landscape: z.boolean().default(true),
      widthMm: z.number().min(50).max(2000).optional(),
      heightMm: z.number().min(50).max(2000).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_drafting"),
      ...placed,
      kind: z.enum([
        "polyline",
        "circle",
        "curve",
        "wall",
        "door",
        "window",
        "leader",
      ]),
      points,
      ...style,
      thickness: canvasLength.default(16),
      width: canvasLength.default(80),
      flip: z.boolean().default(false),
      label: z.string().max(160).default(""),
      wallId: canvasShapeId.optional(),
      segment: z.number().int().min(0).max(999).default(0),
      position: z.number().min(0).max(1).default(0.5),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_mark"),
      ...placed,
      kind: z.enum(["dimension", "area", "angle", "polygon"]),
      points,
      ...style,
    })
    .strict(),
  z
    .object({
      type: z.literal("add_stroke"),
      ...placed,
      kind: z.enum(["draw", "highlight"]),
      points,
      color: canvasColors.default("black"),
      size: size.default("m"),
      closed: z.boolean().default(false),
      fill: z.enum(["none", "solid", "semi", "pattern"]).default("none"),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_link"),
      ...placed,
      kind: z.enum(["bookmark", "embed"]).default("bookmark"),
      url: z.string().url().startsWith("https://").max(2000),
      w: canvasLength.default(300),
      h: canvasLength.default(160),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_arrow"),
      ...placed,
      end: point,
      color: canvasColors.default("black"),
      size: size.default("m"),
      text: text.default(""),
      bend: z.number().min(-10000).max(10000).default(0),
      startHead: z
        .enum([
          "none",
          "arrow",
          "triangle",
          "square",
          "dot",
          "diamond",
          "inverted",
          "bar",
          "pipe",
        ])
        .default("none"),
      endHead: z
        .enum([
          "none",
          "arrow",
          "triangle",
          "square",
          "dot",
          "diamond",
          "inverted",
          "bar",
          "pipe",
        ])
        .default("arrow"),
      kind: z.enum(["arc", "elbow"]).default("arc"),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_note"),
      ...placed,
      text,
      color: canvasColors.default("yellow"),
      size: size.default("m"),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_cloud"),
      ...placed,
      w: canvasLength,
      h: canvasLength,
      color: canvasColors.default("red"),
      text: text.default(""),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_trace"),
      ...placed,
      w: canvasLength,
      h: canvasLength,
      name: z.string().max(120).default("Tracing paper"),
      paperOpacity: z.number().min(0).max(1).default(0.58),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_document"),
      ...placed,
      name: z.string().min(1).max(120),
      w: canvasLength.default(720),
      h: z.number().min(50).max(30000).default(900),
      paragraphs: z.array(paragraph).max(500),
      fontSize: z.number().min(10).max(48).default(16),
    })
    .strict(),
  z
    .object({
      type: z.literal("edit_document"),
      id: canvasShapeId,
      expectedVersion: z.number().int().nonnegative(),
      paragraphs: z.array(paragraph).max(500),
      name: z.string().min(1).max(120).optional(),
    })
    .strict(),
  ...tableOperations,
  z
    .object({
      type: z.literal("calibrate"),
      id: canvasShapeId,
      a: point,
      b: point,
      meters: z.number().positive().max(100000000),
    })
    .strict(),
  z
    .object({
      type: z.literal("set_properties"),
      id: canvasShapeId,
      name: z.string().min(1).max(120).optional(),
      text: text.optional(),
      color: canvasColors.optional(),
      hexColor: hex.optional(),
      fill: z.enum(["none", "solid", "semi", "pattern"]).optional(),
      size: size.optional(),
      opacity: z.number().min(0).max(1).optional(),
      rotation: z
        .number()
        .finite()
        .min(-Math.PI * 2)
        .max(Math.PI * 2)
        .optional(),
      flipX: z.boolean().optional(),
      flipY: z.boolean().optional(),
      paperOpacity: z.number().min(0).max(1).optional(),
      hidden: z.boolean().optional(),
      order: z.number().int().nonnegative().optional(),
      thickness: canvasLength.optional(),
      width: canvasLength.optional(),
      label: z.string().max(160).optional(),
      weight: z.number().min(0.25).max(24).optional(),
      fontSize: z.number().min(8).max(96).optional(),
      bold: z.boolean().optional(),
      italic: z.boolean().optional(),
      align: z.enum(["start", "middle", "end"]).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("set_appearance"),
      id: canvasShapeId,
      backgroundColor: hex.optional(),
      iterationDate: z.string().max(30).optional(),
      revision: z.string().max(80).optional(),
      mask: z.enum(["none", "round", "ellipse"]).optional(),
      borderWidth: z.number().min(0).max(20).optional(),
      borderColor: hex.optional(),
      paperRemoval: z.number().min(0).max(1).optional(),
      altText: z.string().max(1000).optional(),
      font: z.enum(["draw", "sans", "serif", "mono"]).optional(),
      lineHeight: z.number().min(0.5).max(3).optional(),
      dash: z.enum(["draw", "solid", "dashed", "dotted"]).optional(),
      unit: z.enum(["m", "cm", "mm", "ft", "in"]).optional(),
      precision: z.number().int().min(0).max(4).optional(),
      showLabel: z.boolean().optional(),
      points: points.optional(),
      flip: z.boolean().optional(),
      arrowheadStart: z
        .enum([
          "none",
          "arrow",
          "triangle",
          "square",
          "dot",
          "diamond",
          "inverted",
          "bar",
          "pipe",
        ])
        .optional(),
      arrowheadEnd: z
        .enum([
          "none",
          "arrow",
          "triangle",
          "square",
          "dot",
          "diamond",
          "inverted",
          "bar",
          "pipe",
        ])
        .optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("edit_comment"),
      id: z.string().regex(/^comment:[a-zA-Z0-9_-]{1,150}$/),
      text: z.string().min(1).max(12000).optional(),
      deleted: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("set_locked"),
      id: canvasShapeId,
      locked: z.boolean(),
    })
    .strict(),
  z
    .object({
      type: z.literal("crop_image"),
      id: canvasShapeId,
      topLeft: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict(),
      bottomRight: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict(),
    })
    .strict(),
  z
    .object({ type: z.literal("delete_objects"), id: canvasShapeId, ids })
    .strict(),
  z
    .object({
      type: z.literal("duplicate_objects"),
      id: canvasShapeId,
      ids,
      dx: canvasCoordinate.default(24),
      dy: canvasCoordinate.default(24),
    })
    .strict(),
  z
    .object({ type: z.literal("group_objects"), id: canvasShapeId, ids })
    .strict(),
  z.object({ type: z.literal("ungroup_objects"), id: canvasShapeId }).strict(),
  z
    .object({
      type: z.literal("arrange_objects"),
      id: canvasShapeId,
      ids,
      action: z.enum([
        "left",
        "right",
        "top",
        "bottom",
        "center-horizontal",
        "center-vertical",
        "distribute-horizontal",
        "distribute-vertical",
        "front",
        "back",
        "grid",
      ]),
    })
    .strict(),
  z
    .object({ type: z.literal("reparent"), id: canvasShapeId, ...parent })
    .strict(),
  z
    .object({
      type: z.literal("reply_comment"),
      id: z.string().regex(/^comment-thread:[a-zA-Z0-9_-]{1,120}$/),
      commentId: z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/),
      text: z.string().min(1).max(12000),
    })
    .strict(),
  z
    .object({
      type: z.literal("resolve_comment"),
      id: z.string().regex(/^comment-thread:[a-zA-Z0-9_-]{1,120}$/),
      resolved: z.boolean(),
    })
    .strict(),
] as const;

export const workspaceOperation = z.discriminatedUnion(
  "type",
  workspaceOperationSchemas,
);
export type WorkspaceOperation = z.infer<typeof workspaceOperation>;
export function workspaceOperationTargets(op: {
  type: string;
  id: string;
  ids?: string[];
  parentId?: string;
  wallId?: string;
}) {
  return [
    ...new Set([
      ...(op.type.startsWith("add_") ||
      op.type === "create_sheet" ||
      op.type === "group_objects" ||
      op.type === "duplicate_objects"
        ? []
        : [op.id]),
      ...(op.ids ?? []),
      ...(op.wallId ? [op.wallId] : []),
      ...(op.parentId ? [op.parentId] : []),
    ]),
  ];
}
